import { useState, useEffect, useRef } from "react";
import supabase from "../../lib/supabase";

const TIER_LABELS = { compact: "Compact", midsize: "Mid-Size", executive: "Executive", luxury: "Luxury" };

function ChassisHistoryModal({ user, currentWorkspace = null, onClose, onOpenChassis, lang = 'EN', t = {} }: any) {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openingId, setOpeningId] = useState<any>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const runQuery = async () => {
        const q = supabase.from("chassis_history")
          .select("id, business_name, business_input, tier, tokens_consumed, lang, created_at")
          .is("parent_chassis_id", null);
        // In a shared workspace, list every member's chassis (RLS scopes the
        // result to workspace members). In Personal, restrict to the current
        // user's own rows.
        if (currentWorkspace) q.eq("workspace_id", currentWorkspace.id);
        else q.eq("user_id", user.id).is("workspace_id", null);
        q.order("created_at", { ascending: false }).limit(50);
        return q;
      };
      let { data, error } = await runQuery();
      if (error) {
        // Auth token may be stale — refresh session and retry once
        await supabase.auth.getSession();
        const retry = await runQuery();
        data = retry.data;
      }
      setHistory((data as any[]) || []);
      setLoading(false);
    })();
  }, [user.id, currentWorkspace?.id]);

  return (
    <div ref={overlayRef} onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1200,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", width: "100%", maxWidth: 580,
        maxHeight: "88vh", display: "flex", flexDirection: "column",
        boxShadow: "0 20px 60px rgba(0,0,0,0.35)" }}>
        {/* Header */}
        <div style={{ background: "#000", padding: "22px 28px", flexShrink: 0, position: "relative" }}>
          <button onClick={onClose} style={{ position: "absolute", top: 14, right: 14,
            background: "none", border: "none", cursor: "pointer", color: "#555", fontSize: 18 }}>✕</button>
          <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", fontFamily: "'Georgia', serif" }}>
            {currentWorkspace ? currentWorkspace.name : (t?.menuPersonal || 'Personal')}
          </div>
        </div>
        {/* List */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", fontFamily: "'Courier New', monospace",
              fontSize: 11, color: "#aaa", letterSpacing: "0.1em" }}>{t?.historyLoading || 'LOADING...'}</div>
          ) : history.length === 0 ? (
            <div style={{ padding: 48, textAlign: "center" }}>
              <div style={{ fontSize: 36, marginBottom: 16 }}>◎</div>
              <div style={{ fontFamily: "'Courier New', monospace", fontSize: 12, color: "#aaa",
                letterSpacing: "0.1em" }}>{t?.historyEmpty || 'NO CHASSIS YET'}</div>
              <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: "#888", marginTop: 10, lineHeight: 1.6 }}>
                {t?.historyEmptyDesc || 'Generate your first Chassis and it will appear here.'}
              </p>
            </div>
          ) : history.map((ch, i) => (
            <div key={ch.id || i}
              style={{ padding: "16px 24px", borderBottom: "1px solid #f0f0f0",
                display: "flex", alignItems: "center", gap: 14,
                background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#000", marginBottom: 5,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {ch.business_name || ch.business_input || (t?.historyUnnamed || 'Unnamed Chassis')}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ fontFamily: "'Courier New', monospace", fontSize: 9, fontWeight: 900,
                    border: "1px solid #000", padding: "2px 7px", color: "#000" }}>
                    {(TIER_LABELS as any)[ch.tier] || ch.tier}
                  </span>
                  <span style={{ fontFamily: "'Courier New', monospace", fontSize: 10, color: "#888" }}>
                    {ch.tokens_consumed} tkn
                  </span>
                  <span style={{ fontFamily: "'Courier New', monospace", fontSize: 10, color: "#bbb" }}>
                    {ch.lang || "EN"}
                  </span>
                  <span style={{ fontFamily: "'Courier New', monospace", fontSize: 10, color: "#bbb" }}>
                    {ch.created_at ? new Date(ch.created_at).toLocaleDateString() : ""}
                  </span>
                </div>
              </div>
              <button
                disabled={openingId === ch.id}
                onClick={async () => {
                  setOpeningId(ch.id);
                  const { data: full } = await supabase
                    .from("chassis_history")
                    .select("chassis_data, beyond_profit_data, beyond_profit_selections, business_input, tier, lang, id")
                    .eq("id", ch.id)
                    .single();
                  setOpeningId(null);
                  onOpenChassis(full ?? ch);
                  onClose();
                }}
                style={{ padding: "7px 14px", background: openingId === ch.id ? "#555" : "#000",
                  color: "#fff", border: "none", fontFamily: "'Courier New', monospace",
                  fontSize: 10, fontWeight: 900, cursor: openingId === ch.id ? "default" : "pointer",
                  flexShrink: 0, letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
                {openingId === ch.id ? '...' : `${t?.historyOpen || 'OPEN'} →`}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export { ChassisHistoryModal };
